/**
 * @fileoverview Sales CRM Active Clients API service.
 * @description Calls the backend's Active Clients endpoints (Confluence 3.5.11.27-.35): the KPI tiles
 * (GET /sales/clients/summary), the filter choices (GET /sales/clients/filter-options), the grid, one
 * page at a time (GET /sales/clients), one client's details with its ETag (GET
 * /sales/clients/{tenantId}), the HR Admin invitation commands (POST .../hr-admin-invitation and
 * .../hr-admin-invitation/resend), and Edit (PATCH), Archive (POST .../deactivate) and Restore (POST
 * .../restore). Every command takes its `Idempotency-Key` from the caller (see
 * useIdempotentMutation), so a retry resends the same key; Edit also sends the details' `tp1.` ETag as
 * `If-Match`, exactly as it was read (it is not the quoted form of the Authorization resources). The
 * backend does all the scoping and every permission decision, so nothing here knows who the caller is.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { MissingVersionError } from '@/features/sales/lib/request-error.utils'
import { withoutBlankFilters } from '@/features/sales/services/prospects.service'
import { httpClient } from '@/shared/api/http-client'

import type {
  ClientDeactivated,
  ClientDetails,
  ClientFilterOptions,
  ClientListFilters,
  ClientListItem,
  ClientListMeta,
  ClientPage,
  ClientRestored,
  ClientSummary,
  EditClientRequest,
  HrAdminInvited,
  HrAdminInviteResent,
  InviteHrAdminRequest,
  VersionedClientDetails,
} from '@/features/sales/types/sales-client.types'
import type { ResponseWithPageMeta } from '@/shared/api/pagination.utils'

/**
 * @description The path of one client, or of one of its sub-resources.
 * @param {string} tenantId - The client.
 * @param {string} [suffix] - The sub-path, e.g. `deactivate`.
 * @returns {string} The URL path.
 */
function clientPath(tenantId: string, suffix?: string): string {
  const base = `/sales/clients/${encodeURIComponent(tenantId)}`
  return suffix ? `${base}/${suffix}` : base
}

/**
 * @description Fetches the KPI tiles; they ignore the grid's search and filters.
 * @returns {Promise<ClientSummary>} The four tile values.
 */
export async function getClientSummary(): Promise<ClientSummary> {
  const { data } = await httpClient.get<ClientSummary>('/sales/clients/summary')
  return data
}

/**
 * @description Fetches the states and owners the grid can be filtered by.
 * @returns {Promise<ClientFilterOptions>} The choices.
 */
export async function getClientFilterOptions(): Promise<ClientFilterOptions> {
  const { data } = await httpClient.get<ClientFilterOptions>('/sales/clients/filter-options')
  return data
}

/**
 * @description Reads the paging meta of a grid response, falling back to what the page itself holds
 * when the backend sent none.
 * @param {ResponseWithPageMeta<ClientListItem[]>} response - The grid's response.
 * @returns {ClientListMeta} The page, page size, total and page count.
 */
function metaOf(response: ResponseWithPageMeta<ClientListItem[]>): ClientListMeta {
  const meta = response.pageMeta as Partial<ClientListMeta> | undefined
  const limit = meta?.limit ?? (response.data.length || 1)
  const total = meta?.total ?? response.data.length
  return {
    page: meta?.page ?? 1,
    limit,
    total,
    totalPages: meta?.totalPages ?? Math.ceil(total / limit),
  }
}

/**
 * @description Fetches one page of the grid, in the backend's order. Blank filters are left out (the
 * backend rejects a blank parameter).
 * @param {ClientListFilters} [filters] - Search, state, owner, status, sort, page and page size.
 * @returns {Promise<ClientPage>} The page's rows and the paging meta.
 */
export async function getClients(filters: ClientListFilters = {}): Promise<ClientPage> {
  const response = await httpClient.get<ClientListItem[]>('/sales/clients', {
    params: withoutBlankFilters(filters),
  })
  return { items: response.data, meta: metaOf(response) }
}

/**
 * @description Fetches one client in full, with the version Edit must send back.
 * @param {string} tenantId - The client.
 * @returns {Promise<VersionedClientDetails>} The client and its ETag.
 * @throws {MissingVersionError} When the response carries no ETag.
 */
export async function getClientDetails(tenantId: string): Promise<VersionedClientDetails> {
  const response = await httpClient.get<ClientDetails>(clientPath(tenantId))
  const etag: unknown = response.headers.etag
  if (typeof etag !== 'string' || etag === '') throw new MissingVersionError()
  return { client: response.data, etag }
}

/**
 * @description Invites the bank's first user, the HR Admin (Invite HR Admin, 3.5.11.31). Needs a
 * recent MFA sign-in (`403 / 2009` otherwise).
 * @param {string} tenantId - The client.
 * @param {InviteHrAdminRequest} request - The invitee's name, email and mobile number.
 * @param {string} idempotencyKey - The command's key, kept by the caller across retries.
 * @returns {Promise<HrAdminInvited>} The invitation sent (queued, not confirmed delivered).
 */
export async function inviteClientHrAdmin(
  tenantId: string,
  request: InviteHrAdminRequest,
  idempotencyKey: string,
): Promise<HrAdminInvited> {
  const { data } = await httpClient.post<HrAdminInvited>(
    clientPath(tenantId, 'hr-admin-invitation'),
    request,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Sends the HR Admin's pending invitation again (Resend HR Admin Invitation, 3.5.11.32).
 * Sends no body, and needs a new key for every click.
 * @param {string} tenantId - The client.
 * @param {string} idempotencyKey - The command's key, kept by the caller across retries.
 * @returns {Promise<HrAdminInviteResent>} The invitation's new expiry and resend counters.
 */
export async function resendClientHrAdminInvite(
  tenantId: string,
  idempotencyKey: string,
): Promise<HrAdminInviteResent> {
  const { data } = await httpClient.post<HrAdminInviteResent>(
    clientPath(tenantId, 'hr-admin-invitation/resend'),
    undefined,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Edits a client (Edit Client, 3.5.11.33; Super Admin only, with a recent MFA
 * sign-in): a full replacement of its sections.
 * @param {string} tenantId - The client.
 * @param {string} etag - The details' `tp1.` ETag, sent as `If-Match`.
 * @param {EditClientRequest} request - The sections.
 * @param {string} idempotencyKey - The command's key, kept by the caller across retries.
 * @returns {Promise<VersionedClientDetails>} The client as saved, with its new ETag.
 * @throws {MissingVersionError} When the response carries no ETag.
 */
export async function editClient(
  tenantId: string,
  etag: string,
  request: EditClientRequest,
  idempotencyKey: string,
): Promise<VersionedClientDetails> {
  const response = await httpClient.patch<ClientDetails>(clientPath(tenantId), request, {
    headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey },
  })
  const next: unknown = response.headers.etag
  if (typeof next !== 'string' || next === '') throw new MissingVersionError()
  return { client: response.data, etag: next }
}

/**
 * @description Archives a client (Deactivate Client, 3.5.11.34; Super Admin only, with a recent MFA
 * sign-in). Every user of the bank is refused from their next request.
 * @param {string} tenantId - The client.
 * @param {string} idempotencyKey - The command's key, kept by the caller across retries.
 * @returns {Promise<ClientDeactivated>} The client's status and when it was archived.
 */
export async function deactivateClient(
  tenantId: string,
  idempotencyKey: string,
): Promise<ClientDeactivated> {
  const { data } = await httpClient.post<ClientDeactivated>(
    clientPath(tenantId, 'deactivate'),
    undefined,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Restores an archived client (Restore Client, 3.5.11.35; Super Admin only, with a
 * recent MFA sign-in).
 * @param {string} tenantId - The client.
 * @param {string} idempotencyKey - The command's key, kept by the caller across retries.
 * @returns {Promise<ClientRestored>} The client's status.
 */
export async function restoreClient(
  tenantId: string,
  idempotencyKey: string,
): Promise<ClientRestored> {
  const { data } = await httpClient.post<ClientRestored>(
    clientPath(tenantId, 'restore'),
    undefined,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return data
}
